import {
    ClickAwayListener,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    ToggleButton,
    ToggleButtonGroup,
    ToggleButtonGroupProps,
    ToggleButtonProps,
    Tooltip
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { memo, MouseEvent, ReactNode, useCallback, useRef, useState } from 'react'


export type GenericTabsProps<T> = Omit<ToggleButtonGroupProps, 'onChange'> & {
    value: T,
    items: Array<{ key: T, label: string, disabled?: boolean, tooltip?: ReactNode }>,
    onChange: (value: T) => void,
    tabProps?: Partial<ToggleButtonProps>
    compact?: boolean
}


function GenericTabs<T extends {}>({ value, items, onChange, tabProps, sx, compact = false }: GenericTabsProps<T>) {
    const anchorRef = useRef<any>(null)
    const [open, setOpen] = useState(false)
    const changeHandler = useCallback((e: MouseEvent<HTMLElement>, val: T) => {
        onChange(val)
        setOpen(false)
    }, [onChange])

    const renderToggleButton = (item: any, compact: boolean = false) => {
        if (item.tooltip) {
            return (
                <Tooltip title={item.tooltip} arrow={true} placement="bottom" key={item.key + (item.tooltip ? 1 : 2)}>
                    <div>
                        {
                            compact ? item.label :
                                <ToggleButton
                                    style={{ height: '100%', whiteSpace: 'nowrap', flexGrow: 1 }}
                                    value={item.key}
                                    disabled={item.disabled}
                                    {...(tabProps || {})}>
                                    {item.label}
                                </ToggleButton>
                        }
                    </div>
                </Tooltip>
            )
        } else {
            return compact ? item.label : (
                <ToggleButton
                    value={item.key}
                    key={item.key + (item.tooltip ? 1 : 2)}
                    disabled={item.disabled}
                    style={{ whiteSpace: 'nowrap' }}
                    {...(tabProps || {
                        sx: { flexGrow: 1 }
                    })}
                >
                    {item.label}
                </ToggleButton>
            )
        }
    }

    return !compact ? (
        <ToggleButtonGroup size={'small'} value={value} exclusive={true} onChange={changeHandler} sx={sx}>
            {items.map((item, i) => (
                renderToggleButton(item)
            ))}
        </ToggleButtonGroup>
    ) : (
        <>
            <ToggleButtonGroup
                size="small"
                ref={anchorRef}
                value={value}
                aria-label="Button group with a nested menu"
                sx={sx}
            >
                <ToggleButton value={value}>{items.find(i => i.key === value)?.label}</ToggleButton>
                <ToggleButton
                    value={'arrow' as any}
                    onClick={() => setOpen(true)}
                >
                    <ArrowDropDownIcon />
                </ToggleButton>
            </ToggleButtonGroup>
            <Popper
                sx={{
                    zIndex: 99
                }}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === 'bottom' ? 'center top' : 'center bottom'
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={() => setOpen(false)}>
                                <MenuList id="split-button-menu" autoFocusItem>
                                    {items.map((item, i) => (
                                        <MenuItem selected={item.key === value}
                                            onClick={() => changeHandler({} as any, item.key)}>
                                            {renderToggleButton(item, compact)}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    )
}

export default memo(GenericTabs) as typeof GenericTabs